import { useState } from 'react';
import { lateWords } from './lateWords.ts';
import type { ProcessModel } from './useProcess.ts';

/**
 * Adding a step of the tree whose phase has closed (design study 21, land A; Jason's answers, 2026-10-07): it comes
 * late, into the phase under way (core `landingPhase`), and the add asks for NSB's reason in one plain field. On a
 * client's job the client reads it and confirms it, the step held until they do; on NSB's own job the reason is
 * enough. Drawn as the job's other questions are (a line asked about), in a bar over the drawing or under the row it
 * was asked from.
 */

export function LateReason({
  model,
  stepKey,
  busy,
  bar = false,
  onAdd,
  onCancel,
}: {
  model: ProcessModel;
  stepKey: string;
  busy: boolean;
  /** Over the drawing, sticky, as a line asked about is; else under the row it was asked from. */
  bar?: boolean;
  onAdd: (reason: string) => void;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState('');
  const w = lateWords(model, stepKey);
  if (!w) return null;
  const own = model.dto.kind === 'internal';
  return (
    <div
      role="dialog"
      aria-label={`Why ${w.title} comes late`}
      className={bar ? 'sticky top-2 z-30 mb-3 rounded-[2px] border border-ink bg-paper px-4 py-2.5' : 'mt-2 rounded-[2px] bg-sheet p-3 text-sm'}
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <span>
          {w.title} comes late: {w.from} is done, so it lands in {w.into}.
        </span>
        <input
          className={`field ${bar ? '!h-10 w-72' : ''}`}
          aria-label="Why it comes late"
          placeholder="Why it comes late"
          value={reason}
          maxLength={500}
          onChange={(e) => setReason(e.target.value)}
        />
        <button type="button" className="link min-h-11" disabled={busy || !reason.trim()} onClick={() => onAdd(reason.trim())}>
          Add it
        </button>
        <button type="button" className="link min-h-11 text-ink-3" onClick={onCancel}>
          Not now
        </button>
      </div>
      <p className="meta mt-1">{own ? 'NSB’s own job: the reason is enough.' : 'The client reads the reason and confirms it: until then it is held, and cannot start.'}</p>
    </div>
  );
}
